import { ControlElement, JsonSchema7, Layout } from "@jsonforms/core";

import { MappableField } from "lib/generated/integration-api";
import {
  ACCOUNT_FIELD_KEY,
  CrmRecordKind,
  CrmRecordSeed,
  lookupEntitySetFor,
  LookupTargets,
  RECORD_FIELD_SPECS,
  RecordFieldSpec,
} from "./recordFields";
import { CrmFieldMapping, CrmObjectMapping, CrmPushRequest } from "./types";

/**
 * The values a picklist field accepts, keyed by form key. Fetched from the CRM, so a field with no
 * entry — or an empty one — is left as free text rather than an empty dropdown the user is stuck on.
 */
export type PicklistOptions = Record<string, { id: string; label: string }[]>;

export type CrmRecordForm = {
  schema: JsonSchema7;
  uischema: Layout;
  object: CrmObjectMapping;
  initialData: Record<string, unknown>;
  /** Form key -> CRM field name, from the customer's mapping. */
  crmFieldByKey: Record<string, string>;
  /** True when the mapping includes an account, so the form asks the user to pick one. */
  hasAccountField: boolean;
  toPushRequest: (formData: Record<string, unknown>) => CrmPushRequest;
};

function schemaPropertyFor(spec: RecordFieldSpec, options: PicklistOptions): JsonSchema7 {
  switch (spec.control) {
    case "number":
      return { type: "number", title: spec.label };
    case "date":
      return { type: "string", format: "date", title: spec.label };
    case "account":
    case "owner":
      // Holds the id of the picked record; the pickers resolve names through the integrations
      // service, and so does the push.
      return { type: "string", title: spec.label };
    case "picklist": {
      const values = options[spec.key] ?? [];
      // `oneOf` is what selects the enum renderer, so a field whose options have not arrived — or
      // which turned out not to be a picklist in this CRM — stays an ordinary text box.
      if (values.length === 0) return { type: "string", title: spec.label };
      return {
        type: "string",
        title: spec.label,
        oneOf: values.map((value) => ({ const: value.id, title: value.label })),
      };
    }
    case "string":
    case "textarea":
      return { type: "string", title: spec.label };
  }
}

/**
 * A CRM field the object cannot be created without, which the mapping does not fill.
 *
 * Collected on the form under the CRM's own name because there is nothing of ours to map it from:
 * a Salesforce Lead insists on a surname and an employer, and a tender has neither. Without this a
 * customer could only find out by being refused, naming a field their form never showed them.
 *
 * A reference is skipped — which record it should point at is not something a text box can answer,
 * and the CRM names it back on the refusal like any other missing field.
 */
function schemaPropertyForCrmField(field: MappableField): JsonSchema7 | undefined {
  switch (field.type) {
    case "number":
      return { type: "number", title: field.name };
    case "date":
      return { type: "string", format: "date", title: field.name };
    case "enum": {
      const values = field.options ?? [];
      if (values.length === 0) return { type: "string", title: field.name };
      return {
        type: "string",
        title: field.name,
        oneOf: values.map((value) => ({ const: value.id, title: value.label })),
      };
    }
    case "string":
      return { type: "string", title: field.name };
    default:
      return undefined;
  }
}

function controlFor(spec: RecordFieldSpec): ControlElement {
  return {
    type: "Control",
    scope: `#/properties/${spec.key}`,
    label: spec.label,
    // isMultiLineControl keys off this option, which is what selects the textarea renderer.
    ...(spec.control === "textarea" ? { options: { multi: true } } : {}),
  };
}

/**
 * Turns a company's field mapping into a JSON-Forms schema plus the translation back to the CRM's
 * own field names. Only mapped fields appear: clearing a row on the configuration page removes the
 * field from the form.
 */
export function buildRecordForm(
  kind: CrmRecordKind,
  mapping: CrmFieldMapping,
  seed: CrmRecordSeed,
  /** What the connected CRM calls the records a reference field points at. */
  targets: LookupTargets,
  picklistOptions: PicklistOptions = {},
  /**
   * Every CRM field the object's create is refused without. One the mapping already fills is
   * marked required on the control that fills it; the rest get a control of their own, ahead of
   * the mapped fields, since the record cannot be created without them.
   */
  requiredCrmFields: MappableField[] = [],
): CrmRecordForm {
  const properties: Record<string, JsonSchema7> = {};
  const elements: ControlElement[] = [];
  const initialData: Record<string, unknown> = {};
  const crmFieldByKey: Record<string, string> = {};

  const mapped = mappedFields(mapping, kind);
  // Worked out before anything is added, because the required fields are rendered first: checking
  // against what has been added so far would always find the form empty.
  const mappedKeys = new Set(mapped.map((spec) => spec.key));
  const keyByCrmField = new Map(mapped.map((spec) => [mapping.fields[spec.key], spec.key]));
  /** Form keys the schema marks required, which is what holds Send until they are filled. */
  const required: string[] = [];

  for (const field of requiredCrmFields) {
    // Already on the form under our own label, so it only has to be marked.
    const filledBy = keyByCrmField.get(field.id);
    if (filledBy) {
      required.push(filledBy);
      continue;
    }

    const property = schemaPropertyForCrmField(field);
    // Keyed by the CRM's own name, which the push then sends it under. A field whose name collides
    // with one of ours is left out: the two would share a control and a key, and whichever was
    // written last would decide what the other one sent. The CRM refuses it and names it, as it
    // does a required reference — neither is marked required, since a control that is not on the
    // form could never be filled and would hold Send for good.
    if (!property || mappedKeys.has(field.id)) continue;

    properties[field.id] = property;
    elements.push({ type: "Control", scope: `#/properties/${field.id}`, label: field.name });
    crmFieldByKey[field.id] = field.id;
    required.push(field.id);
  }

  for (const spec of mapped) {
    properties[spec.key] = schemaPropertyFor(spec, picklistOptions);
    elements.push(controlFor(spec));
    crmFieldByKey[spec.key] = mapping.fields[spec.key];

    if (spec.seed) {
      const seeded = seed[spec.seed];
      if (seeded !== undefined) initialData[spec.key] = seeded;
    }
  }

  // Declared rather than merely rendered, so the form itself holds Send until they are filled —
  // the point being to refuse here rather than let the CRM refuse after the fact.
  const schema: JsonSchema7 = {
    type: "object",
    properties,
    ...(required.length > 0 ? { required } : {}),
  };
  const uischema: Layout = { type: "VerticalLayout", elements };

  return {
    schema,
    uischema,
    object: mapping.object,
    initialData,
    crmFieldByKey,
    hasAccountField: ACCOUNT_FIELD_KEY in crmFieldByKey,
    toPushRequest: (formData) => toPushRequest(mapping.object, crmFieldByKey, formData, targets),
  };
}

/**
 * The fields this record has mapped, ordered the way the form reads.
 *
 * Driven by the field catalogue rather than by what the mapping holds, so a key the mapping carries
 * for another record — or one this version of the app no longer offers — simply does not appear.
 */
function mappedFields(mapping: CrmFieldMapping, kind: CrmRecordKind): RecordFieldSpec[] {
  return RECORD_FIELD_SPECS.filter(
    (spec) => spec.kinds.includes(kind) && !!mapping.fields[spec.key],
  ).sort((first, second) => specOrder(first) - specOrder(second));
}

function specOrder(spec: RecordFieldSpec): number {
  return SPEC_ORDER.indexOf(spec.key);
}

const SPEC_ORDER = [
  "title",
  "salutation",
  "firstName",
  "lastName",
  "jobTitle",
  "jobFunction",
  "seniority",
  "email",
  "phone",
  "buyerName",
  "account",
  "owner",
  "value",
  "expiryDate",
  "clarificationDeadline",
  "submissionDate",
  "startDate",
  "awardDate",
  "stage",
  "description",
];

function toPushRequest(
  object: CrmObjectMapping,
  crmFieldByKey: Record<string, string>,
  formData: Record<string, unknown>,
  targets: LookupTargets,
): CrmPushRequest {
  const fields: CrmPushRequest["fields"] = {};
  const lookups: CrmPushRequest["lookups"] = {};

  for (const [key, crmField] of Object.entries(crmFieldByKey)) {
    const value = formData[key];
    if (value === undefined || value === null || value === "") continue;

    const entitySetName = lookupEntitySetFor(key, targets);
    if (entitySetName) {
      if (typeof value !== "string") continue;
      lookups[crmField] = { id: value, entitySetName };
      continue;
    }

    if (typeof value === "string" || typeof value === "number") {
      fields[crmField] = value;
    }
  }

  return {
    entitySetName: object.entitySetName,
    logicalName: object.logicalName,
    fields,
    lookups,
  };
}
